import React, { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Breadcrumbs } from "components/navigation";
import { NotificationInline, NotificationToaster } from "components/notifications";
import { ShowHide } from "components/layout";
import { Intent } from "@blueprintjs/core";
import { LicenceCheckingService } from "services";
import { DeclarationWizard } from "pages/licence-checking/declaration-wizard";

const breadcrumbs = [
    { location: "/home", title: "HOME" },
    { location: "/licence-checking", title: "LICENCE CHECKING" },
    { title: "ON SITE DECLARATION" },
];

export function LicenceCheckingOnSiteDeclaration() {
    const { employeeId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [loadErrorMessage, setLoadErrorMessage] = useState(null);

    // Launchers (e.g. the assessments hub row) can supply per-action destinations via
    // navigation state. Fallbacks preserve behaviour for entry points that don't.
    const returnTo = location.state?.returnTo ?? `/employee/${employeeId}/licence-checking`;
    const cancelTo = location.state?.cancelTo ?? "/licence-checking";

    function loadDetails() {
        return LicenceCheckingService.getOnSiteDeclarationDetails(employeeId);
    }

    function saveDetails(payload) {
        // Backend expects: Employee (int) and DriverIsPresent (bool) — see ConsentDeclaration / LicenceCheckingOnSiteConsentRequest in Employee.Models.
        return LicenceCheckingService.submitOnSiteDeclaration({
            ...payload,
            employee: Number(employeeId),
            driverIsPresent: true,
        });
    }

    function onLoadError(error) {
        const httpForbidden = 403;
        if (error?.response?.status === httpForbidden) {
            setLoadErrorMessage("You do not have access to this driver's declaration.");
            return;
        }
        setLoadErrorMessage("We couldn't load this driver's declaration, please try again.");
    }

    function onSaveSuccess() {
        NotificationToaster.show(Intent.SUCCESS, "Declaration submitted successfully.");
        navigate(returnTo);
    }

    function onCancel() {
        navigate(cancelTo);
    }

    return (
        <div className="row">
            <Breadcrumbs items={breadcrumbs} />
            <h1>On Site Declaration</h1>

            <ShowHide
                evaluator={loadErrorMessage != null}
                show={
                    <NotificationInline
                        allowClose={false}
                        intent={Intent.DANGER}
                        text={loadErrorMessage}
                        show
                    />
                }
                hide={
                    <DeclarationWizard
                        loadDetails={loadDetails}
                        saveDetails={saveDetails}
                        showManagerConfirmation
                        onLoadError={onLoadError}
                        onSaveSuccess={onSaveSuccess}
                        onCancel={onCancel}
                    />
                }
            />
        </div>
    );
}
